// GO4S DS — boîte de confirmation (remplace window.confirm).
// Spec : docs/design-system/REFERENCE.md §4 (modale « confirm » 440px :
// badge help-circle + titre + message, pied ghost Annuler + primary Confirmer ;
// variante destructive : badge et bouton danger).
//
// dsConfirm(message, { tone, title, confirmLabel, cancelLabel, amount, amountLabel, amountTone, detail, payer }) → Promise<boolean>
// turboConfirm also reads data-confirm-title and data-confirm-summary-from
// (what will go out, read on screen and added after the question).
// `amount` puts the sum at stake in large figures between the question and
// the buttons (a cashier confirms what she reads, 11/10/2026). `amountTone:
// "success"` shows it in green — the money coming in at a collection — and
// `detail` adds a small line under it: who it is for, how it is paid
// (owner's request of 29/10/2026). `payer` adds « Reçu au nom de … » under
// it, the name the receipt is made out to (QA, 29/10/2026, as the mobile app).
// Branchée globalement sur Turbo.config.forms.confirm (voir application.js) :
// tous les data-turbo-confirm de la plateforme passent par ici. Les
// suppressions (méthode HTTP delete) basculent automatiquement en variante
// destructive.
import { createIcons, icons as lucideIcons } from "lucide";

const LABELS = {
  fr: { title: "Confirmation", confirm: "Confirmer", cancel: "Annuler" },
  en: { title: "Confirmation", confirm: "Confirm", cancel: "Cancel" },
};

function labels() {
  const locale = document.querySelector('meta[name="current-locale"]')?.content;
  return LABELS[locale] || LABELS.fr;
}

export function dsConfirm(message, options = {}) {
  const { tone = "primary", title, confirmLabel, cancelLabel, amount, amountLabel, amountTone, detail, payer } = options;
  const l = labels();
  const danger = tone === "danger";

  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "ds-overlay ds-overlay--center";
    overlay.style.zIndex = "calc(var(--ds-z-overlay, 100) + 10)";
    overlay.innerHTML = `
      <div class="ds-modal ds-modal--center" role="alertdialog" aria-modal="true"
           aria-labelledby="ds-confirm-title" aria-describedby="ds-confirm-desc"
           tabindex="-1" style="--ds-modal-w:440px;">
        <div class="ds-modal__header">
          <span class="ds-modal__badge ds-modal__badge--${danger ? "danger" : "primary"}">
            <i data-lucide="${danger ? "trash-2" : "help-circle"}" class="ds-icon"></i>
          </span>
          <div style="flex:1; min-width:0;">
            <h3 class="ds-modal__title" id="ds-confirm-title"></h3>
            <p class="ds-modal__desc" id="ds-confirm-desc"></p>
          </div>
        </div>
        <div class="ds-confirm-amount" data-ds-confirm="amount" hidden>
          <span class="ds-confirm-amount__label"></span>
          <strong class="ds-confirm-amount__value"></strong>
          <span class="ds-confirm-amount__detail" hidden></span>
          <span class="ds-confirm-amount__detail" data-ds-confirm="payer" hidden></span>
        </div>
        <div class="ds-modal__footer">
          <button type="button" class="ds-btn ds-btn--ghost" data-ds-confirm="cancel"></button>
          <button type="button" class="ds-btn ${danger ? "ds-btn--danger" : "ds-btn--primary"}" data-ds-confirm="ok"></button>
        </div>
      </div>`;

    // Textes injectés via textContent (jamais innerHTML : le message vient des vues)
    overlay.querySelector("#ds-confirm-title").textContent = title || l.title;
    overlay.querySelector("#ds-confirm-desc").textContent = message || "";
    if (amount) {
      const block = overlay.querySelector('[data-ds-confirm="amount"]');
      block.querySelector(".ds-confirm-amount__label").textContent = amountLabel || "";
      block.querySelector(".ds-confirm-amount__value").textContent = amount;
      if (amountTone === "success") block.classList.add("ds-confirm-amount--success");
      if (detail) {
        const line = block.querySelector(".ds-confirm-amount__detail:not([data-ds-confirm])");
        line.textContent = detail;
        line.hidden = false;
      }
      if (payer) {
        const line = block.querySelector('[data-ds-confirm="payer"]');
        line.textContent = payer;
        line.hidden = false;
      }
      block.hidden = false;
    }
    overlay.querySelector('[data-ds-confirm="cancel"]').textContent = cancelLabel || l.cancel;
    overlay.querySelector('[data-ds-confirm="ok"]').textContent = confirmLabel || l.confirm;

    const previouslyFocused = document.activeElement;
    let settled = false;
    // Une modale DS peut être ouverte SOUS le confirm (ex. bouton Supprimer
    // dans une modale) : dans ce cas, ne pas retirer ds-modal-open à la
    // fermeture, sinon le scroll d'arrière-plan se déverrouille.
    const bodyWasLocked = document.body.classList.contains("ds-modal-open");

    const close = (answer) => {
      if (settled) return;
      settled = true;
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("turbo:before-cache", onBeforeCache);
      overlay.remove();
      if (!bodyWasLocked) document.body.classList.remove("ds-modal-open");
      if (previouslyFocused && previouslyFocused.isConnected) {
        previouslyFocused.focus({ preventScroll: true });
      }
      resolve(answer);
    };

    // Navigation pendant que le confirm est ouvert : fermer AVANT que Turbo ne
    // mette la page en cache, sinon l'overlay est snapshotté et réapparaît en
    // modale fantôme (sans écouteurs) au retour par l'historique — et le
    // keydown capture resterait branché sur la page suivante.
    const onBeforeCache = () => close(false);

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        close(false);
      }
    };

    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) close(false);
    });
    overlay.querySelector('[data-ds-confirm="cancel"]').addEventListener("click", () => close(false));
    overlay.querySelector('[data-ds-confirm="ok"]').addEventListener("click", () => close(true));
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("turbo:before-cache", onBeforeCache);

    document.body.appendChild(overlay);
    document.body.classList.add("ds-modal-open");
    try {
      createIcons({ icons: lucideIcons, attrs: { "stroke-width": 2, "aria-hidden": "true" } });
    } catch (_) { /* no-op */ }
    overlay.querySelector('[data-ds-confirm="ok"]').focus({ preventScroll: true });
  });
}

// Détecte une action destructive pour teinter la modale (méthode HTTP delete),
// que la confirmation vienne d'un formulaire (button_to + _method) ou d'un
// lien data-turbo-method transformé en formulaire par Turbo.
function destructive(formElement, submitter) {
  const method =
    submitter?.getAttribute?.("formmethod") ||
    formElement?.querySelector?.('input[name="_method"]')?.value ||
    formElement?.getAttribute?.("data-turbo-method") ||
    formElement?.getAttribute?.("method") ||
    "";
  return method.toLowerCase() === "delete";
}

// The buttons may name the action (data-confirm-label / data-cancel-label on
// the button or its form): « Annuler / Confirmer » read backwards when the
// action itself is a cancellation (QA PY-SP-24).
function buttonLabel(formElement, submitter, key) {
  return submitter?.dataset?.[key] || formElement?.dataset?.[key] || undefined;
}

// The sum the confirmation is about: written on the button or its form
// (data-confirm-amount), or read when the question is asked from a field or
// a text of the form (data-confirm-amount-from, a selector). Bare digits get
// their spaces and the unit (data-confirm-amount-unit).
function confirmedAmount(formElement, submitter) {
  const written = buttonLabel(formElement, submitter, "confirmAmount");
  if (written) return written;
  const selector = buttonLabel(formElement, submitter, "confirmAmountFrom");
  const source = selector && formElement?.querySelector?.(selector);
  if (!source) return undefined;
  const raw = String(source.value ?? source.textContent ?? "").trim();
  // Nothing typed yet (« 0 F CFA »): no figure rather than a zero to confirm.
  const digits = raw.replace(/\D/g, "");
  if (!raw || (digits && Number(digits) === 0)) return undefined;
  if (!/^\d+$/.test(raw.replace(/\s/g, ""))) return raw;
  const unit = buttonLabel(formElement, submitter, "confirmAmountUnit") || "";
  return `${new Intl.NumberFormat("fr-FR").format(Number(digits))} ${unit}`.trim();
}

// What a field or a text of the form says: the option picked in a select,
// the checked one of radio buttons, what was typed, or the text itself.
function readText(formElement, selector) {
  const nodes = Array.from(formElement?.querySelectorAll?.(selector) || []);
  if (nodes.length === 0) return "";
  const node = nodes.find((n) => n.type === "radio" || n.type === "checkbox" ? n.checked : true);
  if (!node) return "";
  if (node.tagName === "SELECT") return node.selectedOptions?.[0]?.textContent?.trim() || "";
  if (node.type === "radio" || node.type === "checkbox") {
    return (node.closest("label")?.textContent || node.value || "").trim();
  }
  return String(node.value ?? node.textContent ?? "").trim();
}

// The small line under the amount: written (data-confirm-detail, e.g. the
// student's name) then read at the moment of the question
// (data-confirm-detail-from, selectors separated by « | », e.g. the payer
// typed and the method picked), joined by « · ».
function confirmedDetail(formElement, submitter) {
  const written = buttonLabel(formElement, submitter, "confirmDetail");
  const selectors = (buttonLabel(formElement, submitter, "confirmDetailFrom") || "")
    .split("|").map((s) => s.trim()).filter(Boolean);
  const parts = [written, ...selectors.map((selector) => readText(formElement, selector))]
    .map((part) => (part || "").trim()).filter(Boolean);
  return parts.length ? parts.join(" · ") : undefined;
}

// « Reçu au nom de … »: the payer written (data-confirm-payer, a student's
// priority parent) or read at the moment of the question
// (data-confirm-payer-from, the payer typed or picked), put in its sentence
// (data-confirm-payer-template, « … {payer} »). Nothing read, no line.
function confirmedPayer(formElement, submitter) {
  const written = buttonLabel(formElement, submitter, "confirmPayer");
  const selector = buttonLabel(formElement, submitter, "confirmPayerFrom");
  const name = (written || (selector ? readText(formElement, selector) : "") || "").trim();
  if (!name) return undefined;
  const template = buttonLabel(formElement, submitter, "confirmPayerTemplate") || "{payer}";
  return template.replace("{payer}", name);
}

// What will go out, read at the moment of the question
// (data-confirm-summary-from, a selector searched in the form then in the
// page): the live counter of a one-page form (« 42 destinataires »), added
// after the question so the figure confirmed is the one on screen.
function confirmedSummary(formElement, submitter) {
  const selector = buttonLabel(formElement, submitter, "confirmSummaryFrom");
  if (!selector) return "";
  const node = formElement?.querySelector?.(selector) || document.querySelector(selector);
  return (node?.textContent || "").replace(/\s+/g, " ").trim();
}

function withSummary(message, formElement, submitter) {
  const summary = confirmedSummary(formElement, submitter);
  return summary ? `${message} ${summary}`.trim() : message;
}

// Signature attendue par Turbo.config.forms.confirm
export function turboConfirm(message, formElement, submitter) {
  return dsConfirm(withSummary(message, formElement, submitter), {
    title: buttonLabel(formElement, submitter, "confirmTitle"),
    tone: destructive(formElement, submitter) ? "danger" : "primary",
    confirmLabel: buttonLabel(formElement, submitter, "confirmLabel"),
    cancelLabel: buttonLabel(formElement, submitter, "cancelLabel"),
    amount: confirmedAmount(formElement, submitter),
    amountLabel: buttonLabel(formElement, submitter, "confirmAmountLabel"),
    amountTone: buttonLabel(formElement, submitter, "confirmAmountTone"),
    detail: confirmedDetail(formElement, submitter),
    payer: confirmedPayer(formElement, submitter),
  });
}
